import { isNotNullish, isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { IS_SIDEBAR_TYPE_PROP } from '../../modules/gojs-sidebar/templates/common/config/common-prop-names';
import { adjustInsertLink } from '../../modules/gojs-sidebar/templates/link/utils/linking-utils';
import { DRAG_CREATING_LINK_TOOL_NAME, IS_FAKE_DRAG_CREATE_PORT_PROP } from './config';

export class DragCreatingLinkTool extends go.LinkingTool {
  constructor() {
    super();
    this.name = DRAG_CREATING_LINK_TOOL_NAME;
    this.isEnabled = false;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.isUnconnectedLinkValid = true;
  }

  private _fakeStartPort: go.Shape | null = null;
  private oldDefaultCursor?: {
    diagram: string;
    document: string;
  };

  cursor = '';
  allowedClickClass = '';

  startManually() {
    this.diagram.currentTool = this;
    this.isEnabled = true;

    this.doActivate();
  }

  canStart(): boolean {
    return !(
      !this.isEnabled ||
      isNullish(this.diagram) ||
      this.diagram.isReadOnly ||
      this.diagram.isModelReadOnly ||
      !this.diagram.allowLink ||
      (!(this.diagram.model instanceof go.GraphLinksModel) && !(this.diagram.model instanceof go.TreeModel)) ||
      !this.diagram.lastInput.left ||
      (this.diagram.currentTool !== this && !this.isBeyondDragSize())
    );
  }

  doActivate() {
    this.diagram.isMouseCaptured = true;
    this.oldDefaultCursor = {
      diagram: this.diagram.defaultCursor,
      document: document.body.style.cursor,
    };
    this.diagram.defaultCursor = this.cursor;
    document.body.style.cursor = this.cursor;
    this.isActive = true;
  }

  doMouseDown() {
    if (isNullish(this.diagram) || !this.isAllowedClick() || !this.isActive) {
      return;
    }
    this.createFakePort();
    super.doActivate();
    super.doMouseDown();
  }

  createFakePort() {
    const port = this.findLinkablePort();
    if (isNullish(port)) {
      const $ = go.GraphObject.make;
      this._fakeStartPort = this.startObject = $(go.Shape, { width: 1, height: 1, portId: '', fromLinkable: true });
      const node = $(
        go.Node,
        { layerName: 'Tool', locationSpot: go.Spot.Center, location: this.diagram.firstInput.documentPoint },
        this.startObject,
      );
      node.data = { [IS_FAKE_DRAG_CREATE_PORT_PROP]: true };
      this.diagram.add(node);
      node.ensureBounds();
    }
  }

  findLinkablePort() {
    const port = super.findLinkablePort();

    if (isNotNullish(port)) {
      return port;
    }

    const node = this.diagram.findObjectAt(
      this.diagram.firstInput.documentPoint,
      (obj: go.GraphObject) => obj.part,
      (part: go.Part) => part.data[IS_SIDEBAR_TYPE_PROP],
    );

    if (isNullish(node)) {
      return null;
    }

    const distance = Math.max(node.actualBounds.width, node.actualBounds.height) / 2 - 1;
    const portOnTheNode = this.diagram
      .findObjectsNear(
        this.diagram.firstInput.documentPoint,
        distance,
        null,
        (obj) => !!(obj?.toLinkable && obj?.part === node),
      )
      ?.first();

    return portOnTheNode;
  }

  insertLink(fromNode: go.Node, fromPort: go.GraphObject, toNode: go.Node, toPort: go.GraphObject) {
    let fNode: go.Node | null = fromNode;
    let fPort: go.GraphObject | null = fromPort;
    if (this._fakeStartPort !== null) {
      fNode = fPort = null;
    }
    const link = super.insertLink(fNode, fPort, toNode, toPort);
    if (link !== null) {
      link.defaultFromPoint = this.diagram.firstInput.documentPoint.copy();
      Object.entries(this.archetypeLinkData || {}).forEach(([key, value]) =>
        this.diagram.model.set(link.data, key, value),
      );
      adjustInsertLink(link, fNode, fPort, toNode, toPort);
    }
    return link;
  }

  doStop() {
    if (this._fakeStartPort !== null) {
      if (this._fakeStartPort?.part) {
        this.diagram.remove(this._fakeStartPort?.part);
      }
      this._fakeStartPort = null;
    }
    super.doStop();
  }

  doDeactivate() {
    super.doDeactivate();
    if (this.oldDefaultCursor) {
      document.body.style.cursor = this.oldDefaultCursor.document;
      this.diagram.defaultCursor = this.oldDefaultCursor.diagram;
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.doCancel();
    }
  }

  private isAllowedClick() {
    const { div, lastInput } = this.diagram;
    const clickedOnCanvas = lastInput?.event?.target instanceof Node && div?.contains(lastInput.event.target);
    const clickedOnAllowedClass =
      isNotNullish(this.allowedClickClass) &&
      lastInput?.event?.target instanceof Element &&
      lastInput.event.target.classList.contains(this.allowedClickClass);

    return clickedOnCanvas || clickedOnAllowedClass;
  }
}
